/* eslint-disable fp/no-mutation */
import React, { ReactElement, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import LockIcon from '@mui/icons-material/Lock'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import { useForm } from '@tanstack/react-form'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  clearCourseAnccUpdateStatus,
  fetchCourseV2ANCCAccreditation,
  fetchCourseV2Compliances,
  selectCourseV2ANCCUpdateStatusByCourseID,
  updateCourseV2ANCCAccreditation,
} from 'src/v3/content-pipeline/course-pipeline.slice'
import { ANCCAccreditation, ContentComplianceStatus } from 'src/v3/core/types'
import {
  ANCC_TARGET_AUDIENCES,
  ANCC_EDUCATIONAL_NEEDS_OPTIONS,
} from '../constants'

type Props = {
  courseID: string
  accreditation: ANCCAccreditation
  fieldName: keyof Pick<
    ANCCAccreditation,
    | 'courseCode'
    | 'expirationDate'
    | 'targetAudience'
    | 'educationalNeeds'
    | 'competency'
    | 'professionalSource'
  >
  fieldLabel: string
  fieldType: 'text' | 'date' | 'textarea' | 'multiselect'
  complianceStatus?: ContentComplianceStatus
  required?: boolean
  onClose?: () => void
}

export default ({
  courseID,
  accreditation,
  fieldName,
  fieldLabel,
  fieldType,
  complianceStatus,
  required = false,
  onClose,
}: Props): ReactElement => {
  let dispatch = useAppDispatch()
  let [isEditing, setIsEditing] = useState(false)
  let [confirmOpen, setConfirmOpen] = useState(false)
  let isApproved = complianceStatus === 'approved'

  let updateStatus = useAppSelector(
    selectCourseV2ANCCUpdateStatusByCourseID(courseID),
  )
  let loading = updateStatus === 'pending'

  let currentValue = accreditation?.[fieldName] || ''
  if (fieldType === 'date' && currentValue) {
    let date = new Date(currentValue as string)
    if (!Number.isNaN(date.getTime())) {
      ;[currentValue] = [date.toISOString().split('T')[0]]
    }
  }

  let form = useForm({
    defaultValues: { [fieldName]: currentValue },
    onSubmit: ({ value }) => {
      let updateValue = value[fieldName]
      if (fieldType === 'date' && updateValue) {
        updateValue = new Date(updateValue as string).toISOString()
      }
      dispatch(
        updateCourseV2ANCCAccreditation({
          courseID,
          id: accreditation.id,
          [fieldName]: updateValue,
        }),
      )
    },
  })

  useEffect(() => {
    if (updateStatus === 'fulfilled') {
      dispatch(fetchCourseV2ANCCAccreditation(courseID))
      dispatch(fetchCourseV2Compliances())
      dispatch(clearCourseAnccUpdateStatus(courseID))
      setIsEditing(false)
      onClose?.()
    }
    if (updateStatus === 'rejected') {
      dispatch(clearCourseAnccUpdateStatus(courseID))
    }
  }, [updateStatus, courseID, dispatch, onClose])

  let renderViewField = () => {
    if (fieldType === 'date') {
      return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            sx={{ bgcolor: 'background.paperDark' }}
            value={
              currentValue && typeof currentValue === 'string'
                ? dayjs(currentValue)
                : null
            }
            disabled
            slotProps={{
              textField: { fullWidth: true },
              openPickerButton: { sx: { display: 'none' } },
            }}
          />
        </LocalizationProvider>
      )
    }
    if (fieldType === 'multiselect') {
      let displayValue = Array.isArray(currentValue)
        ? currentValue.join(', ')
        : String(currentValue || '')
      return (
        <FormControl fullWidth>
          <Select
            sx={{ bgcolor: 'background.paperDark' }}
            value=""
            displayEmpty
            disabled
          >
            <MenuItem value="">{displayValue || <em>Not set</em>}</MenuItem>
          </Select>
        </FormControl>
      )
    }
    if (fieldType === 'textarea') {
      return (
        <TextField
          sx={{ bgcolor: 'background.paperDark' }}
          fullWidth
          multiline
          minRows={3}
          value={currentValue || ''}
          placeholder="Not set"
          disabled
          aria-label={fieldLabel}
        />
      )
    }
    return (
      <TextField
        sx={{ bgcolor: 'background.paperDark' }}
        fullWidth
        value={currentValue || ''}
        placeholder="Not set"
        disabled
        aria-label={fieldLabel}
      />
    )
  }

  let renderEditField = () => {
    if (fieldType === 'date') {
      return (
        <form.Field
          name={fieldName}
          children={(field) => (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                sx={{ bgcolor: 'background.paperDark' }}
                value={
                  field.state.value && typeof field.state.value === 'string'
                    ? dayjs(field.state.value)
                    : null
                }
                onChange={(val) =>
                  field.handleChange(val ? val.format('YYYY-MM-DD') : '')
                }
                onClose={field.handleBlur}
                disabled={loading}
                slotProps={{
                  textField: { fullWidth: true, onBlur: field.handleBlur },
                }}
              />
            </LocalizationProvider>
          )}
        />
      )
    }
    if (fieldType === 'multiselect') {
      let options =
        fieldName === 'targetAudience'
          ? ANCC_TARGET_AUDIENCES
          : fieldName === 'educationalNeeds'
            ? ANCC_EDUCATIONAL_NEEDS_OPTIONS
            : []
      return (
        <form.Field
          name={fieldName}
          children={(field) => (
            <FormControl fullWidth>
              <InputLabel>{fieldLabel}</InputLabel>
              <Select
                sx={{ bgcolor: 'background.paperDark' }}
                multiple
                value={field.state.value || []}
                label={fieldLabel}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                disabled={loading}
                input={<OutlinedInput label={fieldLabel} />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {Array.isArray(selected) &&
                      selected.map((v) => (
                        <Chip key={v} label={v} size="small" />
                      ))}
                  </Box>
                )}
              >
                {options.map((o) => (
                  <MenuItem key={o} value={o}>
                    {o}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />
      )
    }
    if (fieldType === 'textarea') {
      return (
        <form.Field
          name={fieldName}
          children={(field) => (
            <TextField
              sx={{ bgcolor: 'background.paperDark' }}
              fullWidth
              multiline
              minRows={3}
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              disabled={loading}
              aria-label={fieldLabel}
            />
          )}
        />
      )
    }
    return (
      <form.Field
        name={fieldName}
        children={(field) => (
          <TextField
            sx={{ bgcolor: 'background.paperDark' }}
            fullWidth
            value={field.state.value}
            onChange={(e) => field.handleChange(e.target.value)}
            onBlur={field.handleBlur}
            disabled={loading}
            aria-label={fieldLabel}
          />
        )}
      />
    )
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
          minHeight: '24px',
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
          {fieldLabel}
          {required && (
            <Typography color="error" component="span" aria-label="required">
              {' '}
              *
            </Typography>
          )}
        </Typography>
        {isApproved && (
          <LockIcon fontSize="small" sx={{ color: 'success.main' }} />
        )}
      </Box>

      {!isEditing ? (
        <Box sx={{ position: 'relative' }}>
          {renderViewField()}
          <Button
            variant="text"
            size="small"
            onClick={() => {
              if (isApproved) {
                setConfirmOpen(true)
              } else {
                form.setFieldValue(fieldName, currentValue)
                setIsEditing(true)
              }
            }}
            sx={{
              position: 'absolute',
              right: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              minWidth: 'auto',
              px: 1,
            }}
            aria-label={`Edit ${fieldLabel}`}
          >
            Edit
          </Button>
        </Box>
      ) : (
        <Box>
          <Box sx={{ mb: 2 }}>{renderEditField()}</Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => form.handleSubmit()}
              disabled={loading}
              aria-label={`Save changes to ${fieldLabel}`}
            >
              {loading ? 'Saving...' : 'Save'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setIsEditing(false)
                form.reset()
                onClose?.()
              }}
              disabled={loading}
              aria-label={`Cancel editing ${fieldLabel}`}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      )}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        fullWidth
        maxWidth="xs"
        aria-labelledby="ancc-field-confirm-title"
      >
        <DialogTitle id="ancc-field-confirm-title">
          Edit Approved Field
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This field has been approved. Editing will require re-approval.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} variant="outlined">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setConfirmOpen(false)
              form.setFieldValue(fieldName, currentValue)
              setIsEditing(true)
            }}
          >
            Edit Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
